import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Backdrop,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Tab,
  Tabs,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { DocumentSortType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { Pagination } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { Region } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { DEFAULT_SNACKBAR_DURATION } from "../constants";
import {
  copyTemplate,
  listUserInspections,
  listUserTemplates,
  restoreTemplateVersion,
  transferUserInspections,
  transferUserTemplates,
} from "api/endpoints/templates-inspections";
import { GetUserTemplatesAndInspectionsListParams } from "api/endpoints/templates-inspections";
import {
  getUser,
  getUserIdentity as getUserIdentityApi,
} from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import RestrictionsTab from "components/shared/restrictions/restrictions-tab";
import TabPanel from "components/shared/tab-panel";
import UserDevicesTab from "components/user-display/tabs/user-devices-tab";
import UserDiagsTab from "components/user-display/tabs/user-diags-tab";
import UserNotificationsTab from "components/user-display/tabs/user-notifications-tab";
import UserOrgsTab from "components/user-display/tabs/user-orgs-tab";
import UserTemplatesAndInspectionsTab from "components/user-display/tabs/user-templates-and-inspections-tab";
import UserDetails from "components/user-display/user-details";
import UserFeatureFlags from "components/user-display/user-feature-flags";
import { ToMigrationRegion } from "constants/workflow";
import { SnackbarContext } from "context/snackbar-context";
import { UserContext } from "context/user-context";
import {
  UserInspectionListContext,
  UserTemplateListContext,
} from "context/user-templates-and-inspections-context";
import { toUUID } from "helpers/s12-helpers";
import { UserUrlParamTypes } from "types/user-display";

const User = () => {
  const { regionCode, userId } = useParams<UserUrlParamTypes>();
  const { userInspectionsList, setUserInspectionsList } = useContext(
    UserInspectionListContext
  );
  const { userTemplatesList, setUserTemplatesList } = useContext(
    UserTemplateListContext
  );
  const { user, setUser } = useContext(UserContext);
  const [chosenTab, setChosenTab] = useState(1);

  const [transferTemplatesSnackbarShown, setTransferTemplatesSnackbarShown] =
    useState(false);
  const [
    transferInspectionsSnackbarShown,
    setTransferInspectionsSnackbarShown,
  ] = useState(false);
  const [restoreTemplateSnackbarShown, setRestoreTemplateSnackbarShown] =
    useState(false);
  const [copyTemplateSnackbarShown, setCopyTemplateSnackbarShown] =
    useState(false);
  const [searchArchivedTemplates, setSearchArchivedTemplates] = useState(false);
  const [searchArchivedInspections, setSearchArchivedInspections] =
    useState(false);
  const [openProgress, setOpenProgress] = useState(false);

  const [isLoadingGetUserIdentity, setIsLoadingGetUserIdentity] =
    useState<boolean>(false);
  const [identityID, setIdentityID] = useState("");
  const { showError } = useContext(SnackbarContext);

  const getUserInspectionsList = (
    ownerId: string,
    pageSize: number,
    offset: number
  ) => {
    let updatedUserInspectionsList = {
      ...userInspectionsList,
    };
    updatedUserInspectionsList.isLoading = true;
    setUserInspectionsList(updatedUserInspectionsList);
    const ownerUUID = toUUID(ownerId);
    const orgUUID = toUUID(user.orgId);

    const payload: GetUserTemplatesAndInspectionsListParams = {
      ownerId: ownerUUID,
      pageSize: pageSize,
      offset: offset,
      sortDirection: SortDirection.DESC,
      sortType: DocumentSortType.DATE_CREATED,
      noPermissions: true,
      trashed: searchArchivedInspections,
      orgId: orgUUID,
    };

    listUserInspections(payload, callOptionsHeaders(regionCode, userId))
      .then(async (res) => {
        updatedUserInspectionsList = {
          ...userInspectionsList,
          userInspectionsListResponse: res,
          pagination: new Pagination({ pageSize: pageSize, offset: offset }),
        };
      })
      .catch(showError)
      .finally(() => {
        updatedUserInspectionsList.isLoading = false;
        setUserInspectionsList(updatedUserInspectionsList);
      });
  };

  const getUserTemplatesList = (
    ownerId: string,
    pageSize: number,
    offset: number
  ) => {
    let updatedUserTemplatesList = {
      ...userTemplatesList,
    };
    updatedUserTemplatesList.isLoading = true;
    setUserTemplatesList(updatedUserTemplatesList);
    const ownerUUID = toUUID(ownerId);

    const payload: GetUserTemplatesAndInspectionsListParams = {
      ownerId: ownerUUID,
      pageSize: pageSize,
      offset: offset,
      sortDirection: SortDirection.DESC,
      sortType: DocumentSortType.DATE_CREATED,
      noPermissions: true,
      trashed: searchArchivedTemplates,
    };

    listUserTemplates(payload, callOptionsHeaders(regionCode, userId))
      .then(async (res) => {
        updatedUserTemplatesList = {
          ...userTemplatesList,
          userTemplatesListResponse: res,
          pagination: new Pagination({ pageSize: pageSize, offset: offset }),
        };
      })
      .catch(showError)
      .finally(() => {
        updatedUserTemplatesList.isLoading = false;
        setUserTemplatesList(updatedUserTemplatesList);
      });
  };

  const transferInspections = (toID: string, inspectionIDs: string[]) => {
    setUserInspectionsList({
      ...userInspectionsList,
      isLoading: true,
    });

    transferUserInspections(
      {
        fromId: toUUID(userId),
        toId: toUUID(toID),
        inspectionIds: inspectionIDs,
      },
      callOptionsHeaders(regionCode, userId)
    )
      .then(() => {
        setTransferInspectionsSnackbarShown(true);
      })
      .catch(showError)
      .finally(() => {
        getUserInspectionsList(
          userId,
          userInspectionsList.pagination.pageSize,
          0
        );
      });
  };

  const transferTemplates = (toID: string, templateIDs: string[]) => {
    setUserTemplatesList({
      ...userTemplatesList,
      isLoading: true,
    });

    transferUserTemplates(
      {
        fromId: toUUID(userId),
        toId: toUUID(toID),
        templateIds: templateIDs,
      },
      callOptionsHeaders(regionCode, userId)
    )
      .then(() => {
        setTransferTemplatesSnackbarShown(true);
      })
      .catch(showError)
      .finally(() => {
        getUserTemplatesList(userId, userTemplatesList.pagination.pageSize, 0);
      });
  };

  const getUserDetails = () => {
    getUser({ userId }, callOptionsHeaders(regionCode, userId))
      .then((userResp) => {
        if (userResp) {
          setUser(userResp);
        }
      })
      .catch(showError);
  };

  const getUserIdentity = useCallback(() => {
    setIsLoadingGetUserIdentity(true);
    getUserIdentityApi(
      {
        userId,
      },
      callOptionsHeaders(regionCode, userId)
    )
      .then((userIdentityResp) => {
        setIdentityID(userIdentityResp.identityId);
      })
      .catch(showError)
      .finally(() => {
        setIsLoadingGetUserIdentity(false);
      });
  }, [regionCode, userId, showError]);

  const handleRestoreTemplateVersion = (
    inspectionId: string,
    targetUserId: string
  ) => {
    restoreTemplateVersion(
      {
        userId: targetUserId,
        inspectionId,
      },
      callOptionsHeaders(regionCode, userId)
    )
      .then(() => {
        setRestoreTemplateSnackbarShown(true);
      })
      .catch(showError)
      .finally(() => {
        getUserTemplatesList(userId, userTemplatesList.pagination.pageSize, 0);
      });
  };

  const handleCopyTemplate = (
    templateId: string,
    targetUserId: string,
    targetOrgId: string,
    targetRegion: Region
  ) => {
    setOpenProgress(true);
    copyTemplate(
      {
        requestReference: "test",
        sourceOrgId: user.orgId,
        sourceRegion: ToMigrationRegion(regionCode),
        sourceUserId: user.id,
        targetOrgId: targetOrgId,
        targetRegion: targetRegion,
        targetUserId: targetUserId,
        templateId: templateId,
      },
      callOptionsHeaders(regionCode, userId)
    )
      .then(() => {
        setCopyTemplateSnackbarShown(true);
      })
      .catch(showError)
      .finally(() => {
        setOpenProgress(false);
      });
  };

  useEffect(() => {
    getUserDetails();
    getUserIdentity();
    // Adding getUserDetails in dep array would cause infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserIdentity]);

  useEffect(() => {
    getUserTemplatesList(
      userId,
      userTemplatesList.pagination.pageSize,
      userTemplatesList.pagination.offset
    );
    // Adding functions in dep array would cause infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchArchivedTemplates]);

  useEffect(() => {
    if (user?.orgId) {
      getUserInspectionsList(
        userId,
        userInspectionsList.pagination.pageSize,
        userInspectionsList.pagination.offset
      );
    }
    // Adding functions in dep array would cause infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchArchivedInspections, user?.orgId]);

  const handleTabChange = (
    event: React.ChangeEvent<unknown>,
    newValue: number
  ) => {
    setChosenTab(newValue);
  };
  const tabs = [
    {
      label: "Devices",
      component: <UserDevicesTab />,
      enabled: true,
    },
    {
      label: "Organisation",
      component: <UserOrgsTab />,
      enabled: true,
    },
    {
      label: "Flags",
      component: <UserFeatureFlags />,
      enabled: true,
    },
    {
      label: "Owned Documents",
      component: (
        <UserTemplatesAndInspectionsTab
          getUserInspectionsList={(ownerId, pageSize, offset) => {
            getUserInspectionsList(ownerId, pageSize, offset);
          }}
          getUserTemplatesList={(ownerId, pageSize, offset) => {
            getUserTemplatesList(ownerId, pageSize, offset);
          }}
          userInspectionsList={userInspectionsList}
          userTemplatesList={userTemplatesList}
          transferInspections={transferInspections}
          transferTemplates={transferTemplates}
          searchArchivedTemplates={searchArchivedTemplates}
          setSearchArchivedTemplates={(includeArchived) =>
            setSearchArchivedTemplates(includeArchived)
          }
          searchArchivedInspections={searchArchivedInspections}
          setSearchArchivedInspections={(includeArchived) =>
            setSearchArchivedInspections(includeArchived)
          }
          restoreTemplateVersion={handleRestoreTemplateVersion}
          copyTemplate={handleCopyTemplate}
          orgId={user.orgId}
        />
      ),
      enabled: true,
    },
    {
      label: "Diagnostic Files",
      component: <UserDiagsTab />,
      enabled: true,
    },
    {
      label: "Notifications",
      component: <UserNotificationsTab orgId={user?.orgId} />,
      enabled: true,
    },
    {
      label: "Restrictions",
      component: <RestrictionsTab isUser={true} />,
      enabled: true,
    },
  ].filter(({ enabled }) => enabled);
  return (
    <Grid container spacing={3}>
      <UserDetails
        regionCode={regionCode}
        isLoadingGetUserIdentity={isLoadingGetUserIdentity}
        identityID={identityID}
      />

      <Grid item xs={12} lg={12}>
        <Tabs
          value={chosenTab}
          variant="scrollable"
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          {tabs.map((tab) => {
            if (tab.enabled) {
              return <Tab label={tab.label} key={tab.label} />;
            }
            return undefined;
          })}
        </Tabs>
        {tabs.map((tab, index) => {
          return (
            <TabPanel key={tab.label} value={chosenTab} index={index}>
              <Paper>{tab.component}</Paper>
            </TabPanel>
          );
        })}
      </Grid>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={
          transferInspectionsSnackbarShown || transferTemplatesSnackbarShown
        }
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => {
          setTransferInspectionsSnackbarShown(false);
          setTransferTemplatesSnackbarShown(false);
        }}
        message={`Successfully transferred ${
          transferInspectionsSnackbarShown ? "inspection" : "template"
        }(s)`}
        data-testid="transfer-user-document-success"
      />
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={restoreTemplateSnackbarShown}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => {
          setRestoreTemplateSnackbarShown(false);
        }}
        message={"Successfully restored template"}
        data-testid="restore-template-success"
      />
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={copyTemplateSnackbarShown}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => {
          setCopyTemplateSnackbarShown(false);
        }}
        message={"Successfully copied template"}
        data-testid="copy-template-success"
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openProgress}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Grid>
  );
};

export default User;
